import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';

const NAV_LINKS = [
  { label: 'Sobre', href: '#sobre' },
  { label: 'World', href: '#world' },
  { label: 'Multimodal', href: '#multimodal' },
  { label: 'Chat', href: '#chat' },
];

function OrbitalLink({ children, href }: { children: string; href: string }) {
  const spanRef = useRef<HTMLSpanElement>(null);
  const linkRef = useRef<HTMLAnchorElement>(null);

  const handleEnter = () => {
    if (spanRef.current) {
      gsap.fromTo(spanRef.current, { left: '-100%' }, { left: '100%', duration: 0.4, ease: 'power2.inOut' });
    }
  };

  const handleLeave = () => {
    if (spanRef.current) {
      gsap.set(spanRef.current, { left: '-100%' });
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <a
      ref={linkRef}
      href={href}
      onClick={handleClick}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      className="relative overflow-hidden inline-block text-slate-400 text-sm font-medium px-4 py-2 transition-colors duration-200 hover:text-white"
    >
      {children}
      <span
        ref={spanRef}
        className="absolute top-0 left-[-100%] w-full h-full pointer-events-none"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(0, 240, 255, 0.25), transparent)',
        }}
      />
    </a>
  );
}

export default function Navigation() {
  const navRef = useRef<HTMLElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      ref={navRef}
      className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#0a0a0e]/85 backdrop-blur-[40px] border border-white/10'
          : 'bg-[#0a0a0e]/60 backdrop-blur-[40px] border border-transparent'
      }`}
      style={{
        borderRadius: '999px',
        padding: '10px 24px',
        maxWidth: '90vw',
      }}
    >
      <div className="flex items-center gap-6">
        {/* Logo */}
        <a href="#hero" className="font-syncopate text-xs tracking-[0.2em] text-white whitespace-nowrap">
          VÉRIXA
        </a>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <OrbitalLink key={link.href} href={link.href}>
              {link.label}
            </OrbitalLink>
          ))}
        </div>

        {/* CTA Button */}
        <a
          href="#chat"
          className="hidden md:inline-flex items-center justify-center bg-white text-black text-sm font-semibold px-5 py-2 rounded-pill transition-transform duration-200 hover:scale-105"
          onClick={(e) => {
            e.preventDefault();
            document.querySelector('#chat')?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          Iniciar
        </a>

        {/* Mobile Toggle */}
        <button
          className="md:hidden flex flex-col gap-1 p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Menu"
        >
          <span className={`w-5 h-[2px] bg-white transition-transform duration-300 ${mobileOpen ? 'rotate-45 translate-y-[6px]' : ''}`} />
          <span className={`w-5 h-[2px] bg-white transition-opacity duration-300 ${mobileOpen ? 'opacity-0' : ''}`} />
          <span className={`w-5 h-[2px] bg-white transition-transform duration-300 ${mobileOpen ? '-rotate-45 -translate-y-[6px]' : ''}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden mt-4 pb-2 flex flex-col gap-2 border-t border-white/10 pt-4">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-slate-400 text-sm font-medium px-4 py-2 hover:text-white transition-colors"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
                setMobileOpen(false);
              }}
            >
              {link.label}
            </a>
          ))}
        </div>
      )}
    </nav>
  );
}
